import { useLazyGetAvailableRateGroups } from "@/actions/hooks/useLazyGetAvailableRateGroups.ts";
import { AccommodationModalDetailed } from "@/components/AccommodationModalDetailed/AccommodationModalDetailed.tsx";
import Container from "@/components/Container/Container.tsx";
import Modal from "@/components/Modal/Modal.tsx";

import { ReservationSidebar } from "@/components/ReservationSidebar/ReservationSidebar.tsx";
import { Stepper } from "@/components/Stepper/Stepper.tsx";
import { clearReservationOfferURLParam } from "@/helpers/clearReservationOfferURLParam.ts";
import { useGTMAccommodationList } from "@/helpers/gtm/useGTMAccommodationList.ts";
import { useGTMBeginCheckout } from "@/helpers/gtm/useGTMBeginCheckout.ts";
import { URLParam } from "@/helpers/interfaces/URLParam.ts";
import { useScrollToTop } from "@/helpers/scrollToTop.ts";
import { RateGroup } from "@/helpers/types/types.ts";
import { useCartDrawerState } from "@/helpers/useCartDrawerState.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.tsx";
import { useModalState } from "@/helpers/useModalState.ts";
import { PickingHeader } from "@/pages/AccommodationPicking/components/PickingHeader/PickingHeader.tsx";
import { RateGroupsList } from "@/pages/AccommodationPicking/components/RateGroupsList/RateGroupsList.tsx";
import { RPCancelationPolicyModalDetailed } from "@/pages/AccommodationPicking/components/RPCancelationPolicyModalDetailed/RPCancelationPolicyModalDetailed.tsx";
import { ViewAllAccommodationsButton } from "@/pages/AccommodationPicking/components/ViewAllAccommodationsButton/ViewAllAccommodationsButton.tsx";
import { useCheckCartAccommodationAvailibility } from "@/pages/AccommodationPicking/hooks/useCheckCartAccommodationAvailibility.ts";
import { useClearRateGroupURLParams } from "@/pages/AccommodationPicking/hooks/useRateGroupURLParams.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { ModalKey } from "@/stores/ModalStore/ModalKey.ts";
import { useModalStore } from "@/stores/ModalStore/useModalStore.ts";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const AccommodationPicking = () => {
  const { t } = useTranslation();
  const scrollToTop = useScrollToTop();
  const { modalState, handleCloseModal, handleOpenModal } = useModalState<RateGroup>();
  const {
    open,
    closeModal: handleCloseRPCancelationPolicyModalState,
    key,
  } = useModalStore(
    useShallow((state) => ({ open: state.open, closeModal: state.closeModal, key: state.key })),
  );
  const { getOrganizationLoading } = useGetOrganization();
  const { handleCloseCartDrawer } = useCartDrawerState();
  const clearRateGroupURLParams = useClearRateGroupURLParams();
  const {
    availableRateGroups,
    setAvailableRateGroups,
    setPage,
    defaultRateGroups,
    accommodationTypeId,
    setAccommodationTypeId,
    cartAccommodationItems,
  } = useEngineStore(
    useShallow((state) => ({
      defaultRateGroups: state.defaultRateGroups,
      availableRateGroups: state.availableRateGroups,
      setAvailableRateGroups: state.setAvailableRateGroups,
      setPage: state.setPage,
      accommodationTypeId: state.accommodationTypeId,
      setAccommodationTypeId: state.setAccommodationTypeId,
      cartAccommodationItems: state.cartAccommodationItems,
    })),
  );
  const { handleInitialFetchAvailableRGs, handleFetchAvailableRGs, getAvailableRGsLoading } =
    useLazyGetAvailableRateGroups();

  const fetchAvailableRgsAndClearURLParams = async () => {
    setAccommodationTypeId(undefined);
    await new Promise<void>((resolve) => {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete(URLParam.AccommodationType);
      window.history.replaceState(window.history.state, "", `?${urlParams.toString()}`);
      resolve();
    });
    setAvailableRateGroups([]);
    clearReservationOfferURLParam();
    await clearRateGroupURLParams();
    return await handleFetchAvailableRGs();
  };

  useGTMAccommodationList();
  const availabilityDisabled = useCheckCartAccommodationAvailibility();
  const handleGTMBeginCheckout = useGTMBeginCheckout();

  useEffect(() => {
    // fetches initial data if URL params are present
    void handleInitialFetchAvailableRGs();
  }, [handleInitialFetchAvailableRGs]);

  const shouldShowViewAllButton = useMemo(() => {
    return !(
      (defaultRateGroups.length <= 0 && !accommodationTypeId) ||
      getAvailableRGsLoading ||
      getOrganizationLoading
    );
  }, [defaultRateGroups.length, accommodationTypeId, getAvailableRGsLoading, getOrganizationLoading]);

  return (
    <div>
      {cartAccommodationItems.length > 0 && <Stepper />}
      <main className="o-page">
        <div className="o-page__inner">
          <PickingHeader handleFetchAvailableRGs={handleFetchAvailableRGs} />
          <section className="accommodations c-accommodations-wrapper">
            <Container>
              <div
                className={`c-accommodations${
                  cartAccommodationItems.length === 0 ? " c-accommodations--no-sidebar" : ""
                }`}
              >
                <div className="c-accommodations__list">
                  <ViewAllAccommodationsButton
                    onClick={fetchAvailableRgsAndClearURLParams}
                    label={t("viewAllAccommodations")}
                    disabled={!shouldShowViewAllButton}
                    loading={getAvailableRGsLoading || getOrganizationLoading}
                  />
                  <RateGroupsList
                    availableRateGroups={availableRateGroups}
                    isLoading={getAvailableRGsLoading || getOrganizationLoading}
                    handleOpenModal={handleOpenModal}
                    showRateSwitcher={true}
                  />
                </div>
                <div className="c-accommodations__summary">
                  <div className="c-accommodations__summary-inner">
                    <ReservationSidebar
                      hideRemoveRate
                      continueBtnPopoverText={
                        availabilityDisabled ? t("availabilityPopoverContent") : undefined
                      }
                      onContinue={() => {
                        setPage(AppPage.ExtraOptions);
                        handleCloseCartDrawer();
                        scrollToTop();
                        handleGTMBeginCheckout();
                      }}
                      continueDisabled={availabilityDisabled}
                    />
                  </div>
                </div>
              </div>
            </Container>
          </section>
        </div>
      </main>
      <Modal isOpen={modalState.open} onClose={handleCloseModal}>
        <AccommodationModalDetailed previewRG={modalState.editObject} handleCloseModal={handleCloseModal} />
      </Modal>
      <Modal
        isOpen={open && key === ModalKey.RPCancelationPolicy}
        onClose={handleCloseRPCancelationPolicyModalState}
      >
        <RPCancelationPolicyModalDetailed />
      </Modal>
    </div>
  );
};
